import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { pdf } from '@react-pdf/renderer';
import { Radar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
} from 'chart.js';

// Contextos
import { useLoader } from '../../contexts/LoaderContext';

// Componentes
import PDFResultado from './PDFResultado';

// Estilos
import styles from './participantetestdatapage.module.css';

// API
import { useResultadoTesteService } from '../../services/resultadoTesteServices';
import { useTurmaService } from '../../services/turmaServices';
import { useEmpresaService } from '../../services/empresaServices';
import { useTesteService } from '../../services/testeServices';

// Constantes
import { URLS } from '../../constants/urls';

ChartJS.register(RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

const DEBUG = false;
const dbg = (...a) => { if (DEBUG) console.log('[ParticipanteTestDataPage]', ...a); };

function ParticipanteTestDataPage() {
  const navigate = useNavigate();
  const { idResultado } = useParams();
  const { showLoader, hideLoader } = useLoader();

  const { obterTurmaPorId } = useTurmaService();
  const { obterEmpresaPorId } = useEmpresaService();
  const { obterTestePorId } = useTesteService();
  const { obterResultadoPorId, readResult } = useResultadoTesteService();

  const graficoRef = useRef();

  // --- Novo: estados separados para TABELAS (por grupo) e para o GRÁFICO/GERAL ---
  const [groupTables, setGroupTables] = useState([]); // [{ id, label, scores:[{profile, percent, color}], totalRaw? }]
  const [chartScores, setChartScores] = useState([]); // agregado geral (overallFromGroups ou overall)
  const [perfilPredominante, setPerfilPredominante] = useState(''); // do agregado geral
  const [dadosRadar, setDadosRadar] = useState(null);

  // Metadados (para cabeçalho/PDF)
  const [turma, setTurma] = useState(null);
  const [empresa, setEmpresa] = useState(null);
  const [empresaLogoBase64, setEmpresaLogoBase64] = useState('');

  const [pdfUrl, setPdfUrl] = useState('');

  const handleBack = () => navigate(-1);

  const carregarImagemBase64 = async (url) => {
    try {
      const response = await fetch(url, { mode: 'cors' });
      const blob = await response.blob();
      const imageBitmap = await createImageBitmap(blob);
      const canvas = document.createElement('canvas');
      canvas.width = 160; canvas.height = 60;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(imageBitmap, 0, 0, 160, 60);
      return canvas.toDataURL('image/png');
    } catch (error) {
      console.error('Erro ao converter imagem:', error);
      return '';
    }
  };

  const gerarImagemGrafico = () => {
    const chart = graficoRef.current;
    if (!chart) return '';
    try {
      // react-chartjs-2 v5: ref aponta para ChartJS instance
      return chart.toBase64Image();
    } catch {
      // fallback se o ref for ao wrapper e não ao chart
      const canvas = chart?.canvas || chart?.canvasRef?.current;
      return canvas?.toDataURL?.('image/png') || '';
    }
  };

  const gerarPDF = async () => {
    const graficoBase64 = gerarImagemGrafico();
    const blob = await pdf(
      <PDFResultado
        predominantLabel={perfilPredominante}
        // Para o PDF, usamos o agregado geral (mesmo do gráfico)
        scores={chartScores}
        graficoBase64={graficoBase64}
        empresa={empresa}
        turma={turma}
        empresaLogoBase64={empresaLogoBase64}
      />
    ).toBlob();

    const url = URL.createObjectURL(blob);
    setPdfUrl(url);
    return url;
  };

  const handleExportPDF = async () => {
    const url = await gerarPDF();
    const link = document.createElement('a');
    link.href = url;
    link.download = 'resultado_teste.pdf';
    link.click();
  };

  // Constrói o dataset do Radar dinamicamente pelos scores
  const buildRadarData = (scoresArr) => {
    const labels = scoresArr.map(s => s.profile);
    const data = scoresArr.map(s => Number(s.percent) || 0);

    return {
      labels,
      datasets: [{
        label: 'Distribuição dos Perfis (Geral)',
        data,
        backgroundColor: 'rgba(0, 0, 0, 0.06)',
        borderColor: '#002D4C',
        borderWidth: 2,
        pointBackgroundColor: (ctx) => {
          const idx = ctx.dataIndex;
          return scoresArr[idx]?.color || '#00AFA9';
        },
        pointBorderColor: '#fff'
      }]
    };
  };

  // Helper de formatação
  const formatPct = (p) => {
    const n = Number(p ?? 0);
    // mantendo nativo em %, com 2 casas; ajuste no CSS depois se quiser vírgula
    return `${n.toFixed(2)}%`;
  };

  useEffect(() => {
    const carregar = async () => {
      showLoader('Carregando dados');

      // 1) Obter metadados do resultado para buscar turma/empresa/teste
      const base = await obterResultadoPorId(idResultado);
      if (!base?.success) {
        console.error('❌ Falha ao obter dados do resultado (metadados).');
        hideLoader();
        return;
      }

      const idTurma = base.data?.idTurma;
      if (idTurma) {
        const turmaRes = await obterTurmaPorId(idTurma);
        if (turmaRes?.success) {
          setTurma(turmaRes.data);

          const idTeste = turmaRes.data?.idTeste;
          if (idTeste) {
            const testeRes = await obterTestePorId(idTeste);
            dbg('🧪 Teste meta:', testeRes);
          }

          const idEmpresa = turmaRes.data?.idEmpresa;
          if (idEmpresa) {
            const empresaRes = await obterEmpresaPorId(idEmpresa);
            if (empresaRes?.success) {
              setEmpresa(empresaRes.data);
              if (empresaRes.data?.logo) {
                const base64 = await carregarImagemBase64(`${URLS.IMG_PATH}${empresaRes.data.logo}`);
                setEmpresaLogoBase64(base64);
              }
            }
          }
        }
      }

      // 2) Resultado detalhado
      const det = await readResult(idResultado);
      if (!(det?.success && det.data)) {
        console.error('❌ Falha ao ler resultado detalhado via readResult.');
        hideLoader();
        return;
      }

      const data = det.data;

      // --- Tabelas por grupo ---
      const groups = Array.isArray(data.groups) ? data.groups : [];
      const mappedGroups = groups.map(g => ({
        id: g.id,
        label: g.label || g.id,
        totalRaw: g.totalRaw,
        scores: (g.scores || []).map(s => ({
          profile: s.profile,
          percent: Number(s.percent) || 0,
          color: s.color || '#00AFA9'
        }))
      }));
      setGroupTables(mappedGroups);

      // --- Agregado geral (para gráfico e "perfil predominante") ---
      // Se overallFromGroups.available, usa ele; senão fallback para overall.scores; se ainda não, fallback para scores raiz
      const hasOverallFromGroups = data.overallFromGroups?.available && Array.isArray(data.overallFromGroups?.scores);
      const generalScores = hasOverallFromGroups
        ? data.overallFromGroups.scores
        : (data.overall?.scores || data.scores || []);

      const chartSrc = (generalScores || []).map(s => ({
        profile: s.profile,
        percent: Number(s.percent) || 0,
        color: s.color || '#00AFA9'
      }));
      setChartScores(chartSrc);
      setDadosRadar(buildRadarData(chartSrc));

      // Perfil predominante
      const predFromGroups = data.overallFromGroups?.predominant;
      const predOverall = data.overall?.predominant;

      const predArr = (predFromGroups?.profiles?.length ? predFromGroups.profiles
                    : (predOverall?.profiles?.length ? predOverall.profiles : []));
      const predominantLabel = predArr.length ? predArr.join(' + ') : (predFromGroups?.label || predOverall?.label || '—');
      setPerfilPredominante(predominantLabel);

      hideLoader();
    };

    carregar();
  }, [idResultado]);

  // Regerar PDF quando os insumos principais estiverem prontos (opcional)
  useEffect(() => {
    if (empresa && empresaLogoBase64 && turma && perfilPredominante && chartScores.length && dadosRadar) {
      gerarPDF();
    }
    // mantive o trigger original baseado na logo; ajuste se desejar
  }, [empresaLogoBase64]); 

  return (
    <>
      <div className={styles.pageWrapper}>
        <div className={styles.containerTop}>
          <h1 className={styles.title}>Obrigado(a) por finalizar o teste!</h1>

          <p className={styles.turmaTitulo}>
            Seu perfil predominante foi:{' '}
            <strong style={{ color: (chartScores.find(s =>
              perfilPredominante?.toUpperCase().includes(String(s.profile).toUpperCase())
            )?.color) || '#00AFA9' }}>
              {perfilPredominante}
            </strong>
          </p>

          {/* ======= Múltiplas Tabelas (uma por grupo) ======= */}
          <div className={styles.resultadoTabelaWrapper /* CSS ajustará grid/linhas com wrap */}>
            {groupTables.length > 0 ? (
              groupTables.map((g, gi) => (
                <table key={g.id || gi} className={styles.resultadoTabela}>
                  <thead>
                    <tr>
                      <th className={styles.resultadoCabecalho} colSpan={2}>
                        {g.label}
                      </th>
                    </tr>
                    <tr>
                      <th className={styles.resultadoCabecalho}>RESULTADO</th>
                      <th className={styles.resultadoCabecalho}>PERCENTUAL</th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.scores.map((s, i) => {
                      const linhaPar = i % 2 === 0;
                      const cor = s.color || '#00AFA9';
                      // Mantendo o mesmo padrão de rótulo antigo (“Grupo XX - Perfil”),
                      // mas agora faz mais sentido mostrar apenas o perfil.
                      const rotulo = s.profile;
                      return (
                        <tr key={`${g.id}-${s.profile}`} className={linhaPar ? styles.resultadoLinhaPar : styles.resultadoLinha}>
                          <td className={styles.resultadoCelula} style={{ color: cor }}>{rotulo}</td>
                          <td className={styles.resultadoCelula} style={{ color: cor }}>
                            {formatPct(s.percent)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ))
            ) : (
              // Fallback: se não houver groups, mostra uma única tabela com o agregado geral
              <table className={styles.resultadoTabela}>
                <thead>
                  <tr>
                    <th className={styles.resultadoCabecalho} colSpan={2}>Resultado Geral</th>
                  </tr>
                  <tr>
                    <th className={styles.resultadoCabecalho}>RESULTADO</th>
                    <th className={styles.resultadoCabecalho}>PERCENTUAL</th>
                  </tr>
                </thead>
                <tbody>
                  {chartScores.map((s, i) => {
                    const linhaPar = i % 2 === 0;
                    const cor = s.color || '#00AFA9';
                    const rotulo = s.profile;
                    return (
                      <tr key={s.profile} className={linhaPar ? styles.resultadoLinhaPar : styles.resultadoLinha}>
                        <td className={styles.resultadoCelula} style={{ color: cor }}>{rotulo}</td>
                        <td className={styles.resultadoCelula} style={{ color: cor }}>
                          {formatPct(s.percent)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* ======= Um único gráfico (Agregado Geral) — posicione abaixo das tabelas via CSS ======= */}
        {dadosRadar && (
          <div className={styles.graficoContainer}>
            <Radar
              ref={graficoRef}
              data={dadosRadar}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                  r: {
                    beginAtZero: true,
                    min: 0,
                    max: 100,
                    ticks: {
                      stepSize: 20,
                      color: '#444',
                      backdropColor: 'transparent',
                      font: { size: 12, family: 'Formular' }
                    },
                    pointLabels: {
                      font: { size: 13, family: 'Formular', weight: '600' },
                      color: '#002D4C'
                    },
                    grid: { color: '#ddd' },
                    angleLines: { color: '#ccc' }
                  }
                },
                plugins: {
                  legend: { display: false },
                  tooltip: {
                    callbacks: { label: (ctx) => `${ctx.formattedValue}%` }
                  }
                }
              }}
            />
          </div>
        )}

        <div className={styles.divBtn}>
          <button onClick={handleBack} className={styles.navBackButton}>Voltar</button>
          <button onClick={handleExportPDF} className={styles.botaoExportar}>Baixar em PDF</button>
        </div>
      </div>
    </>
  );
}

export default ParticipanteTestDataPage;
